import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { 
  X, 
  Camera, 
  ScanLine, 
  Upload, 
  Volume2, 
  VolumeX, 
  SwitchCamera, 
  Zap, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Copy, 
  PackageCheck,
  RefreshCw
} from 'lucide-react';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScanSuccess: (scannedCode: string) => void;
  title?: string;
  description?: string;
}

// Play crisp success beep tone using Web Audio API
const playBeepSound = () => {
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12); // E6 note

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) {
    console.warn('Audio tone play failed:', e);
  }
};

// Carrier detector helper for Japanese and International shipping labels
const detectCarrierFromCode = (code: string) => {
  const clean = code.trim().toUpperCase();
  if (/^12\d{10}$/.test(clean) || /^\d{12}$/.test(clean)) {
    return { name: 'Yamato Transport (Kuroneko)', color: 'bg-yellow-500 text-black' };
  }
  if (/^\d{10}$/.test(clean) || /^\d{11}$/.test(clean)) {
    return { name: 'Sagawa Express', color: 'bg-blue-600 text-white' };
  }
  if (/^[a-zA-Z]{2}\d{9}[a-zA-Z]{2}$/.test(clean)) {
    return { name: 'Japan Post / EMS', color: 'bg-red-600 text-white' };
  }
  if (/^\d{10,12}$/.test(clean)) {
    return { name: 'DHL / Japan Post', color: 'bg-amber-500 text-black' };
  }
  if (/^\d{12,15}$/.test(clean)) {
    return { name: 'FedEx / Amazon JP', color: 'bg-purple-600 text-white' };
  }
  return { name: 'Código de Rastreio Detectado', color: 'bg-emerald-600 text-white' };
};

export const BarcodeScannerModal: React.FC<BarcodeScannerModalProps> = ({
  isOpen,
  onClose,
  onScanSuccess,
  title = 'Escanear Etiqueta de Envio',
  description = 'Aponte a câmera do celular para o código de barras ou QR Code do pacote.'
}) => {
  const [cameras, setCameras] = useState<Array<{ id: string; label: string }>>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [scannedResult, setScannedResult] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [copied, setCopied] = useState(false);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'html5qrcode-scanner-container';

  // Fetch available video input cameras
  useEffect(() => {
    if (!isOpen) return;

    Html5Qrcode.getCameras()
      .then((deviceList) => {
        if (deviceList && deviceList.length > 0) {
          const formatted = deviceList.map((cam, index) => ({
            id: cam.id,
            label: cam.label || `Câmera ${index + 1}`
          }));
          setCameras(formatted);

          // Default to back camera if available
          const backCam = formatted.find(c => 
            (c.label || '').toLowerCase().includes('back') || 
            (c.label || '').toLowerCase().includes('traseira') ||
            (c.label || '').toLowerCase().includes('rear') ||
            (c.label || '').toLowerCase().includes('environment')
          );
          setSelectedCameraId(backCam ? backCam.id : formatted[formatted.length - 1].id);
        } else {
          setSelectedCameraId('');
        }
      })
      .catch((err) => {
        console.warn('Erro ao listar câmeras:', err);
      });
  }, [isOpen]);

  // Start or restart camera scanner stream
  useEffect(() => {
    if (!isOpen) {
      stopScanner();
      setScannedResult(null);
      setErrorMessage(null);
      return;
    }

    const timer = setTimeout(() => {
      startScanner();
    }, 300);

    return () => {
      clearTimeout(timer);
      stopScanner();
    };
  }, [isOpen, selectedCameraId]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
      } catch (err) {
        console.warn('Erro ao fechar scanner:', err);
      } finally {
        scannerRef.current = null;
        setIsScanning(false);
      }
    }
  };

  const startScanner = async () => {
    setErrorMessage(null);
    const containerEl = document.getElementById(scannerContainerId);
    if (!containerEl) return;

    try {
      if (scannerRef.current) {
        await stopScanner();
      }

      const html5Qrcode = new Html5Qrcode(scannerContainerId, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
          Html5QrcodeSupportedFormats.PDF_417,
          Html5QrcodeSupportedFormats.ITF
        ],
        verbose: false
      });

      scannerRef.current = html5Qrcode;

      const cameraConfig = selectedCameraId 
        ? { deviceId: { exact: selectedCameraId } }
        : { facingMode: 'environment' };

      const config = {
        fps: 15,
        qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
          const minEdgePercentage = 0.75;
          const minDimension = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minDimension * minEdgePercentage);
          return {
            width: Math.max(220, qrboxSize),
            height: Math.max(160, Math.floor(qrboxSize * 0.65)) // rectangular box optimal for barcodes
          };
        },
        aspectRatio: 1.33333
      };

      await html5Qrcode.start(
        cameraConfig,
        config,
        (decodedText) => {
          handleSuccessfulScan(decodedText);
        },
        (errorMsg) => {
          // Ignore frame scan misses
        }
      );

      setIsScanning(true);
    } catch (err: any) {
      console.error('Falha ao iniciar câmera:', err);
      setIsScanning(false);
      setErrorMessage(
        err?.message || 'Não foi possível acessar a câmera do dispositivo. Verifique as permissões de câmera do navegador.'
      );
    }
  };

  const handleSuccessfulScan = (code: string) => {
    const cleanCode = code.trim();
    if (!cleanCode) return;

    if (soundEnabled) {
      playBeepSound();
    }

    if (navigator.vibrate) {
      try {
        navigator.vibrate([100, 50, 100]);
      } catch (e) {
        // ignore
      }
    }

    setScannedResult(cleanCode);
    stopScanner();
  };

  const handleApplyCode = () => {
    if (scannedResult) {
      onScanSuccess(scannedResult);
      onClose();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsProcessingFile(true);
      setErrorMessage(null);

      // Temporary Html5Qrcode instance for file scanning
      const html5Qrcode = new Html5Qrcode('file-scanner-temp-container', false);
      const imageResult = await html5Qrcode.scanFileV2(file, true);

      if (imageResult && imageResult.decodedText) {
        handleSuccessfulScan(imageResult.decodedText);
      } else {
        setErrorMessage('Nenhum código de barras ou QR Code legível foi encontrado na imagem.');
      }
    } catch (err: any) {
      console.warn('Erro ao escanear arquivo:', err);
      setErrorMessage('Não foi possível identificar um código de barras na imagem enviada.');
    } finally {
      setIsProcessingFile(false);
    }
  };

  const toggleTorch = async () => {
    if (!scannerRef.current || !isScanning) return;
    try {
      const nextTorch = !torchEnabled;
      await scannerRef.current.applyVideoConstraints({
        advanced: [{ torch: nextTorch }] as any
      });
      setTorchEnabled(nextTorch);
    } catch (e) {
      console.warn('Lantern / Flashlight não suportado:', e);
    }
  };

  const copyToClipboard = () => {
    if (scannedResult) {
      navigator.clipboard.writeText(scannedResult);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  const carrierInfo = scannedResult ? detectCarrierFromCode(scannedResult) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in overflow-y-auto">
      {/* Container Temp for File Scan */}
      <div id="file-scanner-temp-container" className="hidden" />

      <div className="relative bg-slate-900 text-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-800 overflow-hidden my-auto flex flex-col">
        
        {/* Header */}
        <div className="p-4 sm:p-5 bg-slate-900 border-b border-slate-800 flex justify-between items-center relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 text-emerald-400">
              <ScanLine className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                {title}
              </h3>
              <p className="text-xs text-slate-400 line-clamp-1">{description}</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              stopScanner();
              onClose();
            }}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Camera Viewport / Scanner Region */}
        <div className="relative bg-black min-h-[300px] max-h-[420px] flex items-center justify-center overflow-hidden">
          
          {/* Scanner Container element */}
          <div 
            id={scannerContainerId} 
            className="w-full h-full object-cover [&>video]:w-full [&>video]:h-full [&>video]:object-cover" 
          />

          {/* Scanner Visual Overlay Frame when Scanning */}
          {isScanning && !scannedResult && (
            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
              {/* Darkened Vignette */}
              <div className="absolute inset-0 bg-black/40" />

              {/* Viewfinder Target Frame */}
              <div className="relative w-[80%] max-w-[280px] h-[180px] rounded-2xl border-2 border-emerald-400/80 shadow-[0_0_20px_rgba(52,211,153,0.3)] flex items-center justify-center overflow-hidden z-20">
                {/* Corner Markers */}
                <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-emerald-400" />
                <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-emerald-400" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-emerald-400" />
                <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-emerald-400" />

                {/* Animated Red/Laser Scan Beam Line */}
                <div className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent shadow-[0_0_10px_#ef4444] animate-[bounce_1.8s_infinite]" />
              </div>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-slate-900/80 backdrop-blur-md px-3 py-1 rounded-full text-[11px] font-mono text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-lg">
                <Sparkles className="w-3 h-3 text-emerald-400 animate-spin" />
                <span>Posicione o código de barras ou QR no quadro</span>
              </div>
            </div>
          )}

          {/* Error / Fallback State */}
          {errorMessage && !scannedResult && (
            <div className="absolute inset-0 z-30 bg-slate-950/90 p-6 flex flex-col items-center justify-center text-center">
              <AlertCircle className="w-12 h-12 text-rose-500 mb-3 animate-bounce" />
              <h4 className="font-bold text-slate-100 text-sm sm:text-base mb-1">Câmera indisponível</h4>
              <p className="text-xs text-slate-400 max-w-xs mb-4 leading-relaxed">{errorMessage}</p>

              <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
                <button
                  type="button"
                  onClick={startScanner}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Tentar Novamente</span>
                </button>

                <label className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all border border-slate-700">
                  <Upload className="w-4 h-4 text-emerald-400" />
                  <span>Enviar Foto da Etiqueta</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Scanned Result State Box */}
          {scannedResult && (
            <div className="absolute inset-0 z-30 bg-slate-950/95 p-6 flex flex-col items-center justify-center text-center animate-fade-in">
              <div className="p-3 bg-emerald-500/20 text-emerald-400 rounded-full mb-3 border border-emerald-500/40">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Código Identificado</span>
              
              <div className="w-full max-w-xs bg-slate-900 border border-emerald-500/40 rounded-2xl p-4 my-2 shadow-inner">
                <div className="font-mono text-lg font-black text-emerald-300 break-all select-all">
                  {scannedResult}
                </div>
                {carrierInfo && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold shadow-sm">
                    <span className={`px-2 py-0.5 rounded-md ${carrierInfo.color}`}>
                      {carrierInfo.name}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 mt-3 w-full max-w-xs">
                <button
                  type="button"
                  onClick={handleApplyCode}
                  className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <PackageCheck className="w-4 h-4" />
                  <span>Usar Código</span>
                </button>

                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="p-3 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-colors border border-slate-700"
                  title="Copiar código"
                >
                  <Copy className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setScannedResult(null);
                    startScanner();
                  }}
                  className="p-3 bg-slate-800 hover:bg-slate-700 text-amber-400 rounded-xl transition-colors border border-slate-700"
                  title="Escanear Outro"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Scanner Controls Toolbar */}
        <div className="p-4 bg-slate-900 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 text-xs">
          {/* Camera Selection Dropdown */}
          {cameras.length > 1 && (
            <div className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-xl px-2.5 py-1.5 text-slate-300">
              <SwitchCamera className="w-4 h-4 text-emerald-400 shrink-0" />
              <select
                value={selectedCameraId}
                onChange={(e) => setSelectedCameraId(e.target.value)}
                className="bg-transparent text-xs text-slate-200 focus:outline-none cursor-pointer max-w-[150px] truncate"
              >
                {cameras.map((cam) => (
                  <option key={cam.id} value={cam.id} className="bg-slate-900 text-slate-200">
                    {cam.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            {/* Flashlight Button */}
            <button
              type="button"
              onClick={toggleTorch}
              disabled={!isScanning || !!scannedResult}
              className={`p-2 rounded-xl border transition-colors flex items-center gap-1 text-xs font-semibold ${
                torchEnabled 
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' 
                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
              } disabled:opacity-40`}
              title="Alternar Lanterna"
            >
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Lanterna</span>
            </button>

            {/* Sound Toggle */}
            <button
              type="button"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-xl border transition-colors ${
                soundEnabled 
                  ? 'bg-slate-800 text-emerald-400 border-slate-700' 
                  : 'bg-slate-800 text-slate-500 border-slate-700'
              }`}
              title={soundEnabled ? 'Som ativado' : 'Som desativado'}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>

            {/* Image File Upload Scan Button */}
            <label className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl border border-slate-700 cursor-pointer transition-colors flex items-center gap-1 text-xs font-semibold">
              <Upload className="w-4 h-4 text-emerald-400" />
              <span className="hidden xs:inline">Foto</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={isProcessingFile}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Footer Japanese Shipping Labels Info */}
        <div className="px-4 py-2 bg-slate-950 text-[11px] text-slate-400 text-center border-t border-slate-900/80 flex items-center justify-center gap-2">
          <span>Suporta etiquetas do Sagawa, Yamato Transport, Japan Post, EMS, DHL e QR Codes.</span>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
